import { useState, FormEvent, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { accountGetInfo, accountUpdateInfo, accountResendConfirmationEmail } from '../../api';
import { toast } from 'sonner';
import { useQuery } from '@tanstack/react-query';

export function ChangeEmailPage() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isSendingConfirmation, setIsSendingConfirmation] = useState(false);
  const [formData, setFormData] = useState({
    newEmail: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Fetch current email info
  const { data: userInfo, isLoading: isLoadingInfo } = useQuery({
    queryKey: ['userInfo'],
    queryFn: () => accountGetInfo(),
  });

  // Update form when user info loads
  useEffect(() => {
    if (userInfo) {
      setFormData({ newEmail: userInfo.email });
    }
  }, [userInfo]);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.newEmail) {
      newErrors.newEmail = 'Email address is required';
    } else if (!validateEmail(formData.newEmail)) {
      newErrors.newEmail = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Check if email is unchanged
    if (userInfo && formData.newEmail === userInfo.email) {
      toast.info('Your email is unchanged.');
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      await accountUpdateInfo({
        requestBody: {
          newEmail: formData.newEmail,
        },
      });

      toast.success('Confirmation link to change email sent. Please check your email.');
      navigate('/account/manage');
    } catch (error) {
      console.error('Failed to change email:', error);
      const e = error as { body?: { errors?: Record<string, unknown> }; message?: string };
      if (e.body?.errors) {
        const apiErrors: Record<string, string> = {};
        for (const [key, messages] of Object.entries(e.body.errors)) {
          apiErrors[key] = Array.isArray(messages) ? messages.join(', ') : String(messages);
        }
        setErrors(apiErrors);
      } else {
        toast.error(e.message || 'Failed to change email. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendConfirmation = async () => {
    if (!userInfo?.email) return;

    setIsSendingConfirmation(true);

    try {
      await accountResendConfirmationEmail({
        requestBody: {
          email: userInfo.email,
        },
      });

      toast.success('Confirmation email sent. Please check your inbox.');
    } catch (error) {
      console.error('Failed to resend confirmation email:', error);
      toast.error('Failed to resend confirmation email. Please try again.');
    } finally {
      setIsSendingConfirmation(false);
    }
  };

  if (isLoadingInfo) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-2/3 mb-8"></div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
            <div className="h-10 bg-gray-200 rounded mb-4"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Change Email</h1>
        <p className="mt-2 text-gray-600">Update your email address for account notifications</p>
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Current Email</h2>
          <div className="mt-2 flex items-center gap-3">
            <p className="text-gray-900">{userInfo?.email}</p>
            {userInfo?.isEmailConfirmed ? (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                Confirmed
              </span>
            ) : (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                Pending Confirmation
              </span>
            )}
          </div>
        </div>

        {!userInfo?.isEmailConfirmed && (
          <div className="px-6 py-4 bg-yellow-50 border-b border-yellow-100">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-sm font-medium text-yellow-800">
                  Email confirmation pending
                </h3>
                <p className="mt-1 text-sm text-yellow-700">
                  Please check your email inbox for a confirmation link. You may need to check your spam folder.
                </p>
                <div className="mt-3">
                  <button
                    type="button"
                    onClick={handleResendConfirmation}
                    disabled={isSendingConfirmation}
                    className="text-sm font-medium text-yellow-800 hover:text-yellow-900 underline disabled:opacity-50"
                  >
                    {isSendingConfirmation ? 'Sending...' : 'Resend confirmation email'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="px-6 py-6 space-y-6">
          {/* New Email */}
          <div>
            <label htmlFor="newEmail" className="block text-sm font-medium text-gray-700">
              New Email Address
            </label>
            <input
              type="email"
              id="newEmail"
              value={formData.newEmail}
              onChange={(e) => setFormData(prev => ({ ...prev, newEmail: e.target.value }))}
              className={`mt-1 block w-full rounded-md shadow-sm ${
                errors.newEmail 
                  ? 'border-red-300 focus:border-red-500 focus:ring-red-500' 
                  : 'border-gray-300 focus:border-smartfield focus:ring-smartfield'
              }`}
              required
              autoComplete="email"
            />
            {errors.newEmail && (
              <p className="mt-1 text-sm text-red-600">{errors.newEmail}</p>
            )}
          </div>

          {/* General Error */}
          {errors.general && (
            <div className="rounded-md bg-red-50 p-4">
              <p className="text-sm text-red-800">{errors.general}</p>
            </div>
          )}

          {/* Form Actions */}
          <div className="flex items-center justify-between pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={() => navigate('/account/manage')}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 bg-smartfield text-white rounded-md hover:bg-smartfield/90 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Sending Confirmation...' : 'Change Email'}
            </button>
          </div>
        </form>
      </div>

      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">Email Change Process</h3>
            <div className="mt-2 text-sm text-blue-700">
              <p>When you change your email address:</p>
              <ul className="list-disc list-inside space-y-1 mt-2">
                <li>A confirmation link will be sent to your new email address</li>
                <li>Click the link to confirm the change</li>
                <li>Your account email will be updated once confirmed</li>
                <li>Make sure you have access to the new email address</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
